<template>
  <div class="w-full">
    <div v-if="contentTemplates && contentTemplates.length">
      <USelectMenu
        v-model="currentValue"
        :items="items"
        label-key="name"
        value-key="idContentTemplate"
        :placeholder="placeholder"
        :filter-fields="['name']"
        :disabled="!items.length"
        :class="fieldClass"
      >
        <template #item="{ item }">
          <div class="flex items-center">
            {{ item.name }}
          </div>
        </template>
      </USelectMenu>

      <div class="flex justify-end mt-1">
        <o-panel-link path="/aftercareTemplate/edit" class="text-primary-500 text-sm" @click="onCreateTemplate">
          Create template
        </o-panel-link>
      </div>
    </div>

    <o-loader
      v-else-if="loading"
      class="mx-auto"
      :scale="0.6"
    />

    <div v-else class="text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-800 rounded-md p-2">
      <p class="text-sm">
        You haven't created any templates yet. Click the button below to do so.
      </p>

      <o-panel-link path="/aftercareTemplate/edit" tag="o-button" variant="primary" size="sm" @click="onCreateTemplate">
        Create template
      </o-panel-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'

import { ContentTemplateType } from '~/_api/models/contentTemplate'

interface Props {
  modelValue?: string | number | null
  filter?: Function | null
  fieldClass?: string
  placeholder?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  filter: null,
  fieldClass: 'text-sm',
  placeholder: undefined,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'update:template', value: any): void
  (e: 'createTemplate'): void
}>()

const { $api } = useNuxtApp()

const contentTemplates = ref<any[] | null>(null)
const loading = ref(false)

const currentValue = computed({
  get () {
    return props.modelValue
  },
  set (value) {
    emit('update:modelValue', value)
  },
})

const items = computed(() => {
  let templates = contentTemplates.value

  if (props.filter && templates) {
    templates = templates.filter(props.filter as any)
  }

  return [...(templates || [])].sort((a: any, b: any) => a.listOrder - b.listOrder)
})

function emitSelectedTemplate (value = props.modelValue) {
  const template = contentTemplates.value?.find((template: any) => template.idContentTemplate === value)

  if (template) {
    emit('update:template', template)
  }
}

watch(() => props.modelValue, emitSelectedTemplate, { immediate: true })

onMounted(() => {
  fetchContentTemplates()
})

async function fetchContentTemplates () {
  loading.value = true
  try {
    const response = await $api.contentTemplate.list({
      page: 1,
      filters: { type: ContentTemplateType.serviceAutomation },
    })
    contentTemplates.value = response.items
    emitSelectedTemplate()
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

function onCreateTemplate () {
  emit('createTemplate')
}
</script>
